import { useEffect } from 'react';
import { PLink as Link } from '../components/PLink.tsx';  /* 英文 / 繁体网址上的站内链接留在同一种语言（lib/locale-paths.ts） */
import { tx, UI, type Trilingual } from '../lib/i18n.ts';
import { L, REMIND_STATUS, type Status } from '../lib/landing-copy.ts';
import { loginHref } from '../lib/guest.ts';
import { PAGE_ICON, pageTitle } from '../lib/pages.ts';
import { useApp } from '../state/app.tsx';
import { Icon } from '../components/Icon.tsx';
import { PlanTable } from '../components/PlanTable.tsx';
import { LandingShell, StatusTag } from '../components/LandingShell.tsx';

/**
 * 产品首页。**只有没登录的人会看到**（登录了的人 `/` 直接进总览）。
 *
 * ════════════════════════════════════════════════════════
 * 这一页要回答的四个问题，按这个顺序
 * ════════════════════════════════════════════════════════
 *   1. 这是什么、跟我有什么关系        → 首屏一句话 + 三张示例提醒
 *   2. 它具体替我做什么、现在能不能用  → 三件事 + 每一页的入口（都标着真实状态）
 *   3. 我要付出什么、它会不会害我      → 怎么用 + 不碰你的钱 + 价格
 *   4. 我还有什么顾虑                  → 常见问题
 * 每一段都给两条路：注册，或者**不注册先进去看** —— 后者是这一版最重要的改动。
 * 以前第一次来的人只能看到一个登录框。
 *
 * 所有文案在 lib/landing-copy.ts，三种语言写全。
 * 功能旁边的状态标签是实话（见那个文件开头的规矩）。
 */

/** 首屏下面「每一页都能先看看」那一格格。页面标题从清单来，不在这里另写一份 */
const PAGE_TILES: ReadonlyArray<{ id: string; body: Trilingual; status: Status }> = [
  { id: 'home', body: L.fHome, status: 'ext' },
  { id: 'rules', body: L.fRules, status: 'ext' },
  { id: 'wallet', body: L.fWallet, status: 'ext' },
  { id: 'tokenwatch', body: L.fToken, status: 'live' },
  { id: 'reminders', body: L.fRemind, status: REMIND_STATUS },
  { id: 'look', body: L.fLook, status: 'live' },
  { id: 'billing', body: L.fBilling, status: 'soon' }
];

const PILLARS: ReadonlyArray<{ icon: string; h: Trilingual; b: Trilingual; status: Status; to: string }> = [
  { icon: 'shield', h: L.p1H, b: L.p1B, status: 'ext', to: '/p/rules' },
  { icon: 'chart', h: L.p2H, b: L.p2B, status: 'live', to: '/p/tokenwatch' },
  { icon: 'clock', h: L.p3H, b: L.p3B, status: REMIND_STATUS, to: '/p/reminders' },
  /* 「叫得醒你」链到价格监控：今天真的会用上「重要警告」的是那里的暴跌警报，
     「价格通知发到哪里」那一张卡也在那一页 */
  { icon: 'bell', h: L.p4H, b: L.p4B, status: 'live', to: '/p/tokenwatch' }
];

/** 事项提醒那一段的时间线。第四步是升级成「重要警告」的那一下，画成醒目的颜色 */
const TIMELINE: ReadonlyArray<{ t: Trilingual; b: Trilingual; strong?: boolean }> = [
  { t: L.tl1T, b: L.tl1B },
  { t: L.tl2T, b: L.tl2B },
  { t: L.tl3T, b: L.tl3B },
  { t: L.tl4T, b: L.tl4B, strong: true },
  { t: L.tl5T, b: L.tl5B }
];

const REMIND_POINTS: ReadonlyArray<{ icon: string; h: Trilingual; b: Trilingual }> = [
  { icon: 'clock', h: L.rm1H, b: L.rm1B },
  { icon: 'vol', h: L.rm2H, b: L.rm2B },
  { icon: 'link', h: L.rm3H, b: L.rm3B },
  { icon: 'server', h: L.rm4H, b: L.rm4B }
];

const STEPS: ReadonlyArray<{ h: Trilingual; b: Trilingual; status?: Status }> = [
  { h: L.s1H, b: L.s1B },
  { h: L.s2H, b: L.s2B },
  { h: L.s3H, b: L.s3B },
  { h: L.s4H, b: L.s4B, status: 'ext' }
];

const TRUST: ReadonlyArray<{ icon: string; h: Trilingual; b: Trilingual }> = [
  { icon: 'ban', h: L.t1H, b: L.t1B },
  { icon: 'lock', h: L.t2H, b: L.t2B },
  { icon: 'eye', h: L.t3H, b: L.t3B },
  { icon: 'alert', h: L.t4H, b: L.t4B }
];

/* 「静音也叫得醒吗」排在前面：它是最常被问、也最容易被说过头的一条。
   导出给预渲染用（src/prerender/entry.tsx 拿它写 FAQPage 结构化数据），两边是同一份 */
export const FAQ: ReadonlyArray<[Trilingual, Trilingual]> = [
  [L.q1, L.a1], [L.q2, L.a2],
  [L.q7, REMIND_STATUS === 'live' ? L.a7Live : L.a7],
  [L.q9, L.a9], [L.q8, L.a8],
  [L.q3, L.a3], [L.q4, L.a4], [L.q5, L.a5], [L.q6, L.a6]
];

/* StatusTag 和 LangSwitch 搬去了 components/LandingShell.tsx ——
   功能介绍页和视频页也要用它们，三份抄一样的东西一定会漂移 */

export function Landing(): React.ReactElement {
  const app = useApp();
  const lang = app.lang;
  const signup = loginHref('/p/home');

  useEffect(() => {
    document.title = tx(L.title, lang);
  }, [lang]);

  /* 顶栏和页脚由 LandingShell 提供 —— 功能介绍页、视频页用的是同一份。
     以前这三处各抄一套，而这几页是**没登录的人唯一看到的东西**：
     少一个入口、或者挂着一条已经不存在的链接，是第一印象里最糟的一类错。 */
  const anchors = [
    { to: '#what', label: tx(L.navWhat, lang), anchor: true },
    { to: '#remind', label: tx(L.navRemind, lang), anchor: true },
    { to: '/features', label: tx(L.navFeatures, lang) },
    { to: '/video', label: tx(L.navVideo, lang) },
    { to: '#price', label: tx(L.navPrice, lang), anchor: true },
    { to: '#faq', label: tx(L.navFaq, lang), anchor: true }
  ];

  return (
    <LandingShell anchors={anchors}>
      <>
        {/* ── 首屏 ── */}
        <section className="lp-hero">
          <div className="lp-hero-t">
            <span className="page-kicker">{tx(L.kicker, lang)}</span>
            <h1>
              {tx(L.h1a, lang)}<br />
              <span className="lp-accent">{tx(L.h1b, lang)}</span>
            </h1>
            {/* 首屏这一段没有状态标签，所以只写今天就能用的 —— 见 landing-copy.ts 的 heroSub */}
            <p className="lp-lead">{tx(REMIND_STATUS === 'live' ? L.heroSubLive : L.heroSub, lang)}</p>
            <div className="lp-cta">
              <Link className="btn primary" to={signup}>{tx(L.signup, lang)}</Link>
              <Link className="btn" to="/p/home">{tx(L.heroEnter, lang)} →</Link>
            </div>
            <div className="lp-trust">{tx(L.trust, lang)}</div>
          </div>

          {/* 三张示例提醒：比一段描述更快说清「它会在什么时候、对你说什么」 */}
          <figure className="lp-preview" aria-label={tx(L.previewLabel, lang)}>
            <figcaption className="lp-preview-l">
              <span className="tag accent">{tx(UI.sample, lang)}</span> {tx(L.previewLabel, lang)}
            </figcaption>
            <div className="lp-alert urgent">
              <span className="lp-alert-tag">{tx(L.pv1Tag, lang)}</span>
              <b>{tx(L.pv1Title, lang)}</b>
              <span>{tx(L.pv1Body, lang)}</span>
            </div>
            <div className="lp-alert info">
              <span className="lp-alert-tag">{tx(L.pv2Tag, lang)}</span>
              <b>{tx(L.pv2Title, lang)}</b>
              <span>{tx(L.pv2Body, lang)}</span>
            </div>
            <div className="lp-alert warn">
              <span className="lp-alert-tag">
                {tx(L.pv3Tag, lang)}
                {REMIND_STATUS !== 'live' ? <> · <StatusTag s={REMIND_STATUS} lang={lang} /></> : null}
              </span>
              <b>{tx(L.pv3Title, lang)}</b>
              <span>{tx(L.pv3Body, lang)}</span>
            </div>
          </figure>
        </section>

        {/* ── 三件事 ── */}
        <section id="what" className="lp-sec">
          <h2>{tx(L.whatH, lang)}</h2>
          <p className="lp-sec-sub">{tx(L.whatSub, lang)}</p>
          <div className="lp-grid two">
            {PILLARS.map(p => (
              <div className="card lp-card" key={p.icon}>
                <div className="lp-card-h">
                  <Icon name={p.icon} />
                  <h3>{tx(p.h, lang)}</h3>
                  <StatusTag s={p.status} lang={lang} />
                </div>
                <p>{tx(p.b, lang)}</p>
                <Link to={p.to} className="lp-more">{tx(L.fLookInside, lang)} →</Link>
              </div>
            ))}
          </div>
        </section>

        {/* ── 事项提醒 ──
            单独一整段：它最容易被低估，也最需要一个具体的例子才讲得清 */}
        <section id="remind" className="lp-sec lp-remind">
          <span className="page-kicker">{tx(L.rmKicker, lang)}</span>
          <h2>
            {tx(L.rmH, lang)}
            {' '}<StatusTag s={REMIND_STATUS} lang={lang} />
          </h2>
          <p className="lp-lead" style={{ marginTop: 14 }}>{tx(L.rmLead, lang)}</p>
          {REMIND_STATUS !== 'live' ? (
            <div className="banner warn" style={{ marginTop: 18, maxWidth: 860 }} role="note">
              <span className="ic"><Icon name="info" /></span>
              <span>{tx(L.rmDevNote, lang)}</span>
            </div>
          ) : null}

          <div className="lp-remind-g">
            <figure className="card lp-tl-card">
              <figcaption className="lp-tl-cap">{tx(L.tlCaption, lang)}</figcaption>
              <ol className="lp-tl">
                {TIMELINE.map((s, i) => (
                  <li key={i} className={s.strong ? 'strong' : undefined}>
                    <b>{tx(s.t, lang)}</b>
                    <span>{tx(s.b, lang)}</span>
                  </li>
                ))}
              </ol>
            </figure>
            <div className="lp-remind-pts">
              {REMIND_POINTS.map(p => (
                <div className="lp-trust-i" key={p.icon}>
                  <Icon name={p.icon} />
                  <h3>{tx(p.h, lang)}</h3>
                  <p>{tx(p.b, lang)}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lp-cta">
            <Link className="btn" to="/p/reminders">{tx(L.fLookInside, lang)} →</Link>
          </div>
        </section>

        {/* ── 每一页都能先看看 ── */}
        <section id="pages" className="lp-sec">
          <h2>{tx(L.pagesH, lang)}</h2>
          <p className="lp-sec-sub">{tx(L.pagesSub, lang)}</p>
          <div className="lp-grid tiles">
            {PAGE_TILES.map(t => {
              const title = pageTitle(t.id);
              /* 订阅那一格的状态**跟着服务器走**，不写死。
                 下单、唯一金额、二维码、订单列表都做完了，但真开放付款是一个
                 环境变量（PAY_OPEN）—— 还没开的时候写「已上线」就是一句错话，
                 因为点进去只有一句「即将开放」。这样开放那天首页不用发版就跟着变。 */
              const status: Status = t.id === 'billing'
                ? (app.plans?.purchasable === true ? 'live' : 'soon')
                : t.status;
              return (
                <Link key={t.id} to={'/p/' + t.id} className="card lp-tile">
                  <div className="lp-card-h">
                    <Icon name={PAGE_ICON[t.id] ?? 'cog'} />
                    <h3>{title ? tx(title, lang) : t.id}</h3>
                  </div>
                  <p>{tx(t.body, lang)}</p>
                  <span className="lp-tile-f">
                    <StatusTag s={status} lang={lang} />
                    <span className="lp-more">{tx(L.fLookInside, lang)} →</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* ── 怎么用 ── */}
        <section id="how" className="lp-sec">
          <h2>{tx(L.howH, lang)}</h2>
          <ol className="lp-steps">
            {STEPS.map((s, i) => (
              <li key={i} className="card">
                <span className="lp-step-n" aria-hidden="true">{i + 1}</span>
                <div>
                  <h3>
                    {tx(s.h, lang)}
                    {s.status ? <> <StatusTag s={s.status} lang={lang} /></> : null}
                  </h3>
                  <p>{tx(s.b, lang)}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* ── 不碰你的钱 ── */}
        <section id="safe" className="lp-sec">
          <h2>{tx(L.safeH, lang)}</h2>
          <div className="lp-grid four">
            {TRUST.map(t => (
              <div className="lp-trust-i" key={t.icon}>
                <Icon name={t.icon} />
                <h3>{tx(t.h, lang)}</h3>
                <p>{tx(t.b, lang)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── 价格 ── */}
        <section id="price" className="lp-sec">
          <h2>{tx(L.priceH, lang)}</h2>
          <p className="lp-sec-sub">{tx(L.priceSub, lang)}</p>
          <div className="card lp-price">
            <PlanTable plans={app.plans} lang={lang} />
          </div>
        </section>

        {/* ── 常见问题 ── 用原生的 details/summary：键盘、读屏软件都不用额外处理 */}
        <section id="faq" className="lp-sec">
          <h2>{tx(L.faqH, lang)}</h2>
          <div className="lp-faq">
            {FAQ.map(([q, a], i) => (
              <details key={i} className="card">
                <summary>{tx(q, lang)}</summary>
                {/* 「要花钱吗」的答案跟着服务器走：付款开放了就不能再说「还没开放」 */}
                <p>{tx(a === L.a1 && app.plans?.purchasable === true ? L.a1Open : a, lang)}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── 收尾 ── */}
        <section className="lp-final">
          <h2>{tx(L.finalH, lang)}</h2>
          <p>{tx(L.finalB, lang)}</p>
          <div className="lp-cta">
            <Link className="btn primary" to={signup}>{tx(L.signup, lang)}</Link>
            <Link className="btn" to="/features">{tx(L.navFeatures, lang)} →</Link>
            <Link className="btn" to="/p/home">{tx(L.heroEnter, lang)} →</Link>
          </div>
        </section>
      </>
    </LandingShell>
  );
}
